import type { BatchNumberForm } from '@poweralpha/common';
import { round, sum } from 'lodash';
import * as yup from 'yup';
import type { SimulateBatchForm } from '../plant/models';
import { transformedNumberSchema } from './number';

export function batchParamsNullTest(
  val: Record<string, unknown>,
  ctx: yup.TestContext<yup.AnyObject>,
): boolean {
  const values = ctx.options.context as SimulateBatchForm;
  if (values.view === 'file') {
    return true;
  }
  const entries = Object.entries(val).map(([, v]) => {
    if (v === null || v === undefined || v === '') {
      return false;
    }
    if (typeof v === 'object') {
      if ('name' in v) {
        // v is file
        return Boolean(v);
      }

      // v is array of entries example: multi ess batch params
      if (Array.isArray(v)) {
        const arrayBatchParams = v.map((batchParam) => {
          const arrayBatchParamEntries = Object.entries(
            batchParam as Record<string, BatchNumberForm>,
          ).map(([, batchParamEntry]) => {
            const { start } = batchParamEntry;
            return !(start === undefined || start === null);
          });
          return sum(arrayBatchParamEntries) > 0;
        });
        return sum(arrayBatchParams) > 0;
      }
      // v is batch param
      const { start } = v as BatchNumberForm;
      if (start === undefined || start === null) {
        return false;
      }
    }
    return true;
  });
  return sum(entries) > 0;
}

export function batchStartRequired(val: unknown, ctx: yup.TestContext<yup.AnyObject>): boolean {
  const parent = ctx.parent as { step?: number; end?: number };
  return val !== undefined || (parent.end === undefined && parent.step === undefined);
}

export function batchStepRequired(val: unknown, ctx: yup.TestContext<yup.AnyObject>): boolean {
  const parent = ctx.parent as { end?: number };
  return val !== undefined || parent.end === undefined;
}

export function batchEndTest(val: unknown, ctx: yup.TestContext<yup.AnyObject>): boolean {
  const parent = ctx.parent as { start?: number; step?: number };
  let start = parent.start !== undefined ? Math.abs(parent.start) : undefined;
  let step = parent.step !== undefined ? Math.abs(parent.step) : undefined;
  let end = val as number | undefined;

  if (start === undefined || (step === undefined && end === undefined)) {
    return true;
  }

  if (end === undefined || step === undefined) {
    return false;
  }

  const isFloat =
    parent.start?.toString().includes('.') ||
    parent.step?.toString().includes('.') ||
    end?.toString().includes('.');

  if (isFloat) {
    const startPre = start.toString().slice(start.toString().indexOf('.') + 1).length;
    const stepPre = step.toString().slice(step.toString().indexOf('.') + 1).length;
    const endPre = end.toString().slice(end.toString().indexOf('.') + 1).length;
    const mult = 10 ** Math.max(startPre, stepPre, endPre);
    start = round(start * mult, 5);
    step = round(step * mult, 5);
    end = round(end * mult, 5);
  }

  return end > start && (end - start) % step === 0;
}

export const getBatchNumberSchema = (
  label: string,
  min: number,
  max: number,
  integer = false,
  required = false,
) => {
  const minText = `${label} must be greater than/equal to ${min}`;
  const maxText = `${label} must be less than/equal to ${max}`;

  let start = transformedNumberSchema
    .min(min, minText)
    .max(max, maxText)
    .test('startReq', 'Start is required if Step or End have a value', batchStartRequired);
  let step = transformedNumberSchema
    .test('stepReq', 'Step is required if End has a value', batchStepRequired)
    .max(max - min, `${label} step must be less than/equal to ${round(max - min, 2)}`);
  let end = transformedNumberSchema
    .test('step', 'End value must be in step increments after start value', batchEndTest)
    .min(min, minText)
    .max(max, maxText);

  if (integer) {
    start = start.integer();
    step = step.integer();
    end = end.integer();
  }

  if (required) {
    start = start.required();
  }

  return yup.object({ start, step, end });
};

const batchInputSchema = yup.object({
  start: transformedNumberSchema,
  step: transformedNumberSchema,
  end: transformedNumberSchema,
});

export type BatchNumberParsed = yup.InferType<typeof batchInputSchema>;
